// React and hooks
import React, { useCallback, useMemo, useState } from "react";

// Types
import { VehicleGroupDropdownProps } from "./types";
import { DropdownOption } from "../Dropdown/types";

// Services and API
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { DropdownCacheKey, buildDropdownCacheKey, useDropdownCache, invalidateDropdownCache } from "services/hooks/useDropdownCache";

// Components
import { DropdownAutocomplete } from "../DropdownAutocomplete";

// API Data Types
import {
  VehicleGroupEditFormValues,
  VehicleGroupRow,
} from "services/api/calculations/vehiclegroup/types";

// API Calls
import {
  createVehicleGroupAPI,
  getVehicleGroupsAPI,
} from "services/api/calculations/vehiclegroup/api";

const FIVE_MINUTES = 5 * 60 * 1000;

const VehicleGroupDropdown: React.FC<VehicleGroupDropdownProps> = (
  props: VehicleGroupDropdownProps
) => {
  const {
    name,
    disabled,
    required,
    label,
    error,
    value,
    direction,
    width,
    height,
    placeholder,
    onChange,
  } = props;

  const [searchValue, setSearchValue] = useState<string>("");

  const { data, isLoading, refresh } = useDropdownCache<DataWithPagination<VehicleGroupRow>>(
    buildDropdownCacheKey(DropdownCacheKey.VehicleGroups, searchValue),
    () => getVehicleGroupsAPI(initialDataWithPagination.pagination, searchValue),
    FIVE_MINUTES
  );

  const searchResults = data ?? initialDataWithPagination;

  const handleSearchClick = useCallback(() => {
    setSearchValue("");
  }, []);

  const handleSearchChange = useCallback((name: string) => {
    setSearchValue(name);
  }, []);

  const handleCreateVehicle = useCallback(
    async (e: any, name: string) => {
      e.preventDefault();

      var vehicleGroupEditFormValues = {
        name,
      } as VehicleGroupEditFormValues;

      var result = await createVehicleGroupAPI(vehicleGroupEditFormValues);
      if (result) {
        invalidateDropdownCache(DropdownCacheKey.VehicleGroups);
        setSearchValue(name);
        refresh();
      }
    },
    [refresh]
  );

  const vehicleGroupItems: DropdownOption[] = useMemo(
    () => searchResults.data.map((i) => ({
      value: i.id,
      text: `${i.name}`,
    })),
    [searchResults.data]
  );

  return (
    <>
      <DropdownAutocomplete
        label={label}
        name={name}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        value={value}
        direction={direction}
        width={width}
        height={height}
        options={vehicleGroupItems}
        onChange={onChange}
        onSearchChange={handleSearchChange}
        onSearchFocus={handleSearchClick}
        error={error}
        onCreate={handleCreateVehicle}
        isLoading={isLoading}
      />
    </>
  );
};

export default React.memo(VehicleGroupDropdown);
